import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ComboboxInput } from "./ui/combobox-input";
import { Loader2, Plus, X, Trash2 } from "lucide-react";
import axios from "axios";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContractFormModal({ open, onOpenChange, contract, onSuccess, filters }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [customFields, setCustomFields] = useState([]);  // Available custom fields definitions
  const [customFieldValues, setCustomFieldValues] = useState({});  // Values for custom fields
  const [newFieldName, setNewFieldName] = useState("");  // New field being added
  const [showAddField, setShowAddField] = useState(false);
  const [formData, setFormData] = useState({
    type: "",
    societe: "",
    produit: "",
    date_signature: "",
    date_installation: "",
    duree_mois: "",
    date_fin: "",
    montant_abonnement: "",
    abonnement_gestion_interne: "",
    abonnement_finance: "",
    leaser: "",
    numero_financement: "",
    ca_leaser: "",
    rachat: "",
    vendeur: "",
    revendeur: "",
    commission: "",
    vente_materiel: "",
    frais_installation: "",
    cout_installation: "",
    location_serveur: "",
    banque: "",
    iban: "",
    bic: ""
  });

  const isEditing = !!contract;

  // Fetch available custom fields
  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const res = await axios.get(`${API}/contracts/custom-fields`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCustomFields(res.data.fields || []);
      } catch (err) {
        console.error("Error fetching custom fields:", err);
      }
    };
    if (open && token) {
      fetchCustomFields();
    }
  }, [open, token]);

  useEffect(() => {
    if (contract) {
      setFormData({
        type: contract.type || "",
        societe: contract.societe || "",
        produit: contract.produit || "",
        date_signature: contract.date_signature || "",
        date_installation: contract.date_installation || "",
        duree_mois: contract.duree_mois?.toString() || "",
        date_fin: contract.date_fin || "",
        montant_abonnement: contract.montant_abonnement?.toString() || "",
        abonnement_gestion_interne: contract.abonnement_gestion_interne?.toString() || "",
        abonnement_finance: contract.abonnement_finance?.toString() || "",
        leaser: contract.leaser || "",
        numero_financement: contract.numero_financement || "",
        ca_leaser: contract.ca_leaser?.toString() || "",
        rachat: contract.rachat?.toString() || "",
        vendeur: contract.vendeur || "",
        revendeur: contract.revendeur || "",
        commission: contract.commission?.toString() || "",
        vente_materiel: contract.vente_materiel?.toString() || "",
        frais_installation: contract.frais_installation?.toString() || "",
        cout_installation: contract.cout_installation?.toString() || "",
        location_serveur: contract.location_serveur?.toString() || "",
        banque: contract.banque || "",
        iban: contract.iban || "",
        bic: contract.bic || ""
      });
      // Load existing custom field values
      setCustomFieldValues(contract.custom_fields || {});
    } else {
      setFormData({
        type: "",
        societe: "",
        produit: "",
        date_signature: "",
        date_installation: "",
        duree_mois: "",
        date_fin: "",
        montant_abonnement: "",
        abonnement_gestion_interne: "",
        abonnement_finance: "",
        leaser: "",
        numero_financement: "",
        ca_leaser: "",
        rachat: "",
        vendeur: "",
        revendeur: "",
        commission: "",
        vente_materiel: "",
        frais_installation: "",
        cout_installation: "",
        location_serveur: "",
        banque: "",
        iban: "",
        bic: ""
      });
      setCustomFieldValues({});
    }
  }, [contract, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCustomFieldChange = (fieldKey, value) => {
    setCustomFieldValues(prev => ({ ...prev, [fieldKey]: value }));
  };

  const handleAddCustomField = async () => {
    if (!newFieldName.trim()) return;
    
    try {
      const res = await axios.post(
        `${API}/contracts/custom-fields?field_name=${encodeURIComponent(newFieldName.trim())}&field_type=text`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.field) {
        // Add to available fields if not already present
        if (!customFields.find(f => f.key === res.data.field.key)) {
          setCustomFields(prev => [...prev, res.data.field]);
        }
        // Initialize value for this field
        setCustomFieldValues(prev => ({ ...prev, [res.data.field.key]: "" }));
      }
      
      setNewFieldName("");
      setShowAddField(false);
    } catch (err) {
      console.error("Error adding custom field:", err);
      alert("Erreur lors de l'ajout du champ");
    }
  };

  const handleRemoveCustomFieldValue = (fieldKey) => {
    setCustomFieldValues(prev => {
      const updated = { ...prev };
      delete updated[fieldKey];
      return updated;
    });
  };

  const handleDeleteCustomField = async (fieldKey, fieldName) => {
    if (!window.confirm(`Supprimer définitivement le champ "${fieldName}" ?\n\nCette action supprimera ce champ de tous les contrats.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/contracts/custom-fields/${fieldKey}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Remove from available fields
      setCustomFields(prev => prev.filter(f => f.key !== fieldKey));
      // Remove value if any
      setCustomFieldValues(prev => {
        const updated = { ...prev };
        delete updated[fieldKey];
        return updated;
      });
    } catch (err) {
      console.error("Error deleting custom field:", err);
      alert("Erreur lors de la suppression du champ");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.type || !formData.societe) {
      alert("Le type et la société sont obligatoires");
      return;
    }

    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const payload = {
        type: formData.type,
        societe: formData.societe,
        produit: formData.produit || null,
        date_signature: formData.date_signature || null,
        date_installation: formData.date_installation || null,
        duree_mois: formData.duree_mois ? parseInt(formData.duree_mois) : null,
        date_fin: formData.date_fin || null,
        montant_abonnement: formData.montant_abonnement ? parseFloat(formData.montant_abonnement) : 0,
        abonnement_gestion_interne: formData.abonnement_gestion_interne ? parseFloat(formData.abonnement_gestion_interne) : 0,
        abonnement_finance: formData.abonnement_finance ? parseFloat(formData.abonnement_finance) : 0,
        leaser: formData.leaser || null,
        numero_financement: formData.numero_financement || null,
        ca_leaser: formData.ca_leaser ? parseFloat(formData.ca_leaser) : 0,
        rachat: formData.rachat ? parseFloat(formData.rachat) : 0,
        vendeur: formData.vendeur || null,
        revendeur: formData.revendeur && formData.revendeur !== "none" ? formData.revendeur : null,
        commission: formData.commission ? parseFloat(formData.commission) : 0,
        vente_materiel: formData.vente_materiel ? parseFloat(formData.vente_materiel) : 0,
        frais_installation: formData.frais_installation ? parseFloat(formData.frais_installation) : 0,
        cout_installation: formData.cout_installation ? parseFloat(formData.cout_installation) : 0,
        location_serveur: formData.location_serveur ? parseFloat(formData.location_serveur) : 0,
        banque: formData.banque || null,
        iban: formData.iban || null,
        bic: formData.bic || null,
        custom_fields: customFieldValues
      };

      if (isEditing) {
        await axios.put(`${API}/contracts/${contract.id}`, payload, { headers });
      } else {
        await axios.post(`${API}/contracts`, payload, { headers });
      }

      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving contract:", error);
      alert(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Modifier le contrat" : "Nouveau contrat"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          {/* Section: Informations principales */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
              Informations principales
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">Type *</Label>
                <ComboboxInput
                  value={formData.type}
                  onValueChange={(v) => handleChange("type", v)}
                  options={filters?.types || []}
                  placeholder="Sélectionner ou saisir un type"
                  emptyText="Aucun type trouvé"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="societe">Société / Client *</Label>
                <ComboboxInput
                  value={formData.societe}
                  onValueChange={(v) => handleChange("societe", v)}
                  options={filters?.clients || []}
                  placeholder="Sélectionner ou saisir une société"
                  emptyText="Aucune société trouvée"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="produit">Produit</Label>
                <ComboboxInput
                  value={formData.produit}
                  onValueChange={(v) => handleChange("produit", v)}
                  options={filters?.produits || []}
                  placeholder="Sélectionner ou saisir un produit"
                  emptyText="Aucun produit trouvé"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="vendeur">Vendeur</Label>
                <ComboboxInput
                  value={formData.vendeur}
                  onValueChange={(v) => handleChange("vendeur", v)}
                  options={filters?.vendeurs || []}
                  placeholder="Sélectionner ou saisir un vendeur"
                  emptyText="Aucun vendeur trouvé"
                />
              </div>
            </div>
          </div>

          {/* Section: Dates */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
              Dates du contrat
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date_signature">Date de signature</Label>
                <Input
                  id="date_signature"
                  type="date"
                  value={formData.date_signature}
                  onChange={(e) => handleChange("date_signature", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="date_installation">Date d'installation</Label>
                <Input
                  id="date_installation"
                  type="date"
                  value={formData.date_installation}
                  onChange={(e) => handleChange("date_installation", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="duree_mois">Durée (mois)</Label>
                <Input
                  id="duree_mois"
                  type="number"
                  value={formData.duree_mois}
                  onChange={(e) => handleChange("duree_mois", e.target.value)}
                  placeholder="Ex: 36"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="date_fin">Date de fin</Label>
                <Input
                  id="date_fin"
                  type="date"
                  value={formData.date_fin}
                  onChange={(e) => handleChange("date_fin", e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Section: Montants */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
              Montants et abonnements
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="montant_abonnement">Abonnement mensuel (€)</Label>
                <Input
                  id="montant_abonnement"
                  type="number"
                  step="0.01"
                  value={formData.montant_abonnement}
                  onChange={(e) => handleChange("montant_abonnement", e.target.value)}
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="abonnement_gestion_interne">Gestion interne (€)</Label>
                <Input
                  id="abonnement_gestion_interne"
                  type="number"
                  step="0.01"
                  value={formData.abonnement_gestion_interne}
                  onChange={(e) => handleChange("abonnement_gestion_interne", e.target.value)}
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="abonnement_finance">Finance / Leasing (€)</Label>
                <Input
                  id="abonnement_finance"
                  type="number"
                  step="0.01"
                  value={formData.abonnement_finance}
                  onChange={(e) => handleChange("abonnement_finance", e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Section: Leasing et commissions */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
              Financement / Leasing
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="leaser">Leaser</Label>
                <ComboboxInput
                  value={formData.leaser}
                  onValueChange={(v) => handleChange("leaser", v)}
                  options={filters?.leasers || []}
                  placeholder="Sélectionner ou saisir un leaser"
                  emptyText="Aucun leaser trouvé"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="numero_financement">N° Financement</Label>
                <Input
                  id="numero_financement"
                  value={formData.numero_financement}
                  onChange={(e) => handleChange("numero_financement", e.target.value)}
                  placeholder="Ex: NON ou numéro..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ca_leaser">CA Leaser (€)</Label>
                <Input
                  id="ca_leaser"
                  type="number"
                  step="0.01"
                  value={formData.ca_leaser}
                  onChange={(e) => handleChange("ca_leaser", e.target.value)}
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="rachat">Rachat (€)</Label>
                <Input
                  id="rachat"
                  type="number"
                  step="0.01"
                  value={formData.rachat}
                  onChange={(e) => handleChange("rachat", e.target.value)}
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="revendeur">Revendeur</Label>
                <ComboboxInput
                  value={formData.revendeur}
                  onValueChange={(v) => handleChange("revendeur", v)}
                  options={filters?.revendeurs || []}
                  placeholder="Sélectionner ou saisir un revendeur"
                  emptyText="Aucun revendeur trouvé"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="commission">Commission (€)</Label>
                <Input
                  id="commission"
                  type="number"
                  step="0.01"
                  value={formData.commission}
                  onChange={(e) => handleChange("commission", e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Section: Installation et coûts */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
              Installation et coûts
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="frais_installation">Frais installation (€)</Label>
                <Input
                  id="frais_installation"
                  type="number"
                  step="0.01"
                  value={formData.frais_installation}
                  onChange={(e) => handleChange("frais_installation", e.target.value)}
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cout_installation">Coût installation (€)</Label>
                <Input
                  id="cout_installation"
                  type="number"
                  step="0.01"
                  value={formData.cout_installation}
                  onChange={(e) => handleChange("cout_installation", e.target.value)}
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="location_serveur">Location serveur (€)</Label>
                <Input
                  id="location_serveur"
                  type="number"
                  step="0.01"
                  value={formData.location_serveur}
                  onChange={(e) => handleChange("location_serveur", e.target.value)}
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="vente_materiel">Vente matériel (€)</Label>
                <Input
                  id="vente_materiel"
                  type="number"
                  step="0.01"
                  value={formData.vente_materiel}
                  onChange={(e) => handleChange("vente_materiel", e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Section: Informations bancaires */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
              Informations bancaires
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="banque">Banque</Label>
                <Input
                  id="banque"
                  value={formData.banque}
                  onChange={(e) => handleChange("banque", e.target.value)}
                  placeholder="Ex: BNP, LCL..."
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="iban">IBAN</Label>
                <Input
                  id="iban"
                  value={formData.iban}
                  onChange={(e) => handleChange("iban", e.target.value)}
                  placeholder="FR76 XXXX XXXX XXXX..."
                  className="font-mono text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bic">BIC</Label>
                <Input
                  id="bic"
                  value={formData.bic}
                  onChange={(e) => handleChange("bic", e.target.value)}
                  placeholder="Ex: BNPAFRPP"
                  className="font-mono"
                />
              </div>
            </div>
          </div>

          {/* Section: Champs personnalisés */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
                Champs personnalisés
              </h3>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={() => setShowAddField(true)}
                className="gap-1"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Ajouter un champ</span>
                <span className="sm:hidden">Ajouter</span>
              </Button>
            </div>

            {/* Add new field form */}
            {showAddField && (
              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <Input
                  value={newFieldName}
                  onChange={(e) => setNewFieldName(e.target.value)}
                  placeholder="Nom du nouveau champ..."
                  className="flex-1"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCustomField())}
                />
                <Button type="button" size="sm" onClick={handleAddCustomField}>
                  <Plus className="w-4 h-4" />
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => { setShowAddField(false); setNewFieldName(""); }}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}

            {/* Existing custom fields */}
            {customFields.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {customFields.map((field) => (
                  <div key={field.key} className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label htmlFor={`custom_${field.key}`} className="flex-1">{field.name}</Label>
                      <div className="flex items-center gap-1">
                        {customFieldValues.hasOwnProperty(field.key) && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-muted-foreground hover:text-orange-500"
                            onClick={() => handleRemoveCustomFieldValue(field.key)}
                            title="Retirer ce champ du contrat"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-muted-foreground hover:text-red-500"
                          onClick={() => handleDeleteCustomField(field.key, field.name)}
                          title="Supprimer définitivement ce champ"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <Input
                      id={`custom_${field.key}`}
                      value={customFieldValues[field.key] || ""}
                      onChange={(e) => handleCustomFieldChange(field.key, e.target.value)}
                      placeholder={`Saisir ${field.name.toLowerCase()}`}
                    />
                  </div>
                ))}
              </div>
            )}

            {customFields.length === 0 && !showAddField && (
              <p className="text-sm text-muted-foreground italic">
                Aucun champ personnalisé. Cliquez sur "Ajouter un champ" pour en créer un.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEditing ? "Enregistrer" : "Créer le contrat"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
